// File: src/blueprint/screens/Entry/EntryScreen.jsx

import { useEffect, useMemo, useRef } from 'react';
import { useBlueprintState } from '../../state/useBlueprintState';
import { usePacks } from '../../state/usePacks';

function asString(value) {
  return value == null ? '' : String(value);
}

function isRunHostPage() {
  try {
    const path = String(window.location.pathname || '').toLowerCase();
    return path === '/run' || path.startsWith('/run/');
  } catch {
    return false;
  }
}

function getPackFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const value = params.get('pack');
    return value ? String(value).trim() : '';
  } catch {
    return '';
  }
}

function openRunnerTab(packId) {
  const id = asString(packId).trim();
  if (!id) return;

  const origin = encodeURIComponent(window.location.href);
  const url = `/run/?pack=${encodeURIComponent(id)}&from=${origin}`;
  window.open(url, '_blank', 'noopener');
}

function findPack(list, packId) {
  const normalizedPackId = asString(packId).trim().toLowerCase();
  if (!normalizedPackId) return null;

  return (
    list.find((pack) => {
      const candidate = asString(pack?.pack_id ?? pack?.id)
        .trim()
        .toLowerCase();
      return candidate === normalizedPackId;
    }) || null
  );
}

function getPackEntryMeta(foundPack, fallbackPackId) {
  const normalizedPackId = asString(fallbackPackId).trim();

  const title =
    asString(foundPack?.title).trim() || (normalizedPackId ? `Pack ${normalizedPackId}` : 'Pack');
  const subtitle =
    asString(foundPack?.subtitle).trim() || asString(foundPack?.description).trim() || '';
  const version = asString(foundPack?.pack_version).trim();
  const time = asString(foundPack?.time).trim();
  const startLabel = asString(foundPack?.startLabel).trim() || 'Start';

  return {
    title,
    subtitle,
    version,
    time,
    startLabel,
  };
}

function formatVersion(version) {
  const value = asString(version).trim();
  if (!value) return '';
  return value.startsWith('v') ? value : `v${value}`;
}

function formatTime(time) {
  const value = asString(time).trim();
  if (!value) return '';
  return `Approx. ${value}`;
}

function resolveEntryUi({ mode, packMeta, progress }) {
  const base = {
    title: packMeta.title,
    subtitle: packMeta.subtitle,
    version: formatVersion(packMeta.version),
    timeLabel: formatTime(packMeta.time),
    primaryLabel: packMeta.startLabel || 'Start',
    secondaryLabel: '',
    lines: [],
  };

  if (mode === 'resume') {
    return {
      ...base,
      title: 'Welcome back',
      primaryLabel: 'Continue',
      lines: [
        `You have completed ${progress}% of ${packMeta.title}.`,
        'Continue where you left off to unlock more insights.',
      ],
    };
  }

  if (mode === 'expired') {
    return {
      ...base,
      title: 'Your session expired.',
      subtitle: '',
      primaryLabel: 'Contact Support',
      secondaryLabel: 'Request new access',
      lines: [
        'You can contact support to reopen your session.',
        'Please keep your support code ready.',
      ],
      version: '',
      timeLabel: '',
    };
  }

  return base;
}

export default function EntryScreen({ mode = 'first', onPrimaryAction, packId = null }) {
  const { statePacks } = usePacks();
  const { state, pilotConsentAccepted } = useBlueprintState();
  const didAutostartRef = useRef(false);

  const showDescription = Boolean(state.showDescription);
  const showTime = Boolean(state.showTime);

  const progress = mode === 'resume' ? 9 : 0;

  const { packMeta, isLoadingPacks } = useMemo(() => {
    const list = Array.isArray(statePacks?.packs) ? statePacks.packs : [];
    const foundPack = findPack(list, packId);

    return {
      packMeta: getPackEntryMeta(foundPack, packId),
      isLoadingPacks: Boolean(statePacks?.isLoading),
    };
  }, [statePacks?.packs, statePacks?.isLoading, packId]);

  const entryUi = useMemo(() => {
    const resolved = resolveEntryUi({
      mode,
      packMeta,
      progress,
    });

    if (isLoadingPacks && resolved.title.startsWith('Pack ')) {
      return {
        ...resolved,
        title: 'Loading…',
      };
    }

    return resolved;
  }, [mode, packMeta, progress, isLoadingPacks]);

  useEffect(() => {
    if (!pilotConsentAccepted) return;
    if (didAutostartRef.current) return;
    if (!isRunHostPage()) return;

    const urlPack = getPackFromUrl();
    if (!urlPack) return;

    const currentPackId = asString(packId).trim();
    if (!currentPackId) return;

    if (currentPackId.toLowerCase() !== urlPack.toLowerCase()) return;

    didAutostartRef.current = true;
    onPrimaryAction?.();
  }, [packId, onPrimaryAction, pilotConsentAccepted]);

  function handlePrimaryAction() {
    if (!pilotConsentAccepted) {
      return;
    }
    if (isRunHostPage()) {
      onPrimaryAction?.();
      return;
    }

    openRunnerTab(packId);
  }

  return (
    <main className="dv-br-main">
      <section className="dv-br-card" aria-label="Entry">
        <h2 className="dv-br-card__title">
          {entryUi.title}{' '}
          {entryUi.version ? (
            <span className="dv-br-card__version">({entryUi.version})</span>
          ) : null}
        </h2>

        {showDescription && entryUi.subtitle ? (
          <div className="dv-br-card__text dv-br-card__muted">{entryUi.subtitle}</div>
        ) : null}

        {showTime && entryUi.timeLabel ? (
          <div className="dv-br-card__text dv-br-card__muted">{entryUi.timeLabel}</div>
        ) : null}

        {entryUi.lines.map((line) => (
          <div key={line} className="dv-br-card__text dv-br-card__muted">
            {line}
          </div>
        ))}

        <div className="dv-br-card__actions">
          <button
            type="button"
            className="dv-br-btn dv-br-btn--primary"
            onClick={handlePrimaryAction}
          >
            {entryUi.primaryLabel}
          </button>

          {entryUi.secondaryLabel ? (
            <button type="button" className="dv-br-btn dv-br-btn--secondary">
              {entryUi.secondaryLabel}
            </button>
          ) : null}
        </div>
      </section>
    </main>
  );
}
